// Utility functions for storing and applying price overrides
// Overrides are loaded once at app start and applied across all data modules

const PRICE_KEYS = new Set([
  'price',
  'base_price',
  'basePrice',
  'price_adjustment',
  'priceAdjustment',
  'combo_price',
]);

const NAME_KEYS = ['name', 'title', 'label'];

let cachedOverrides = {};

export const setPriceOverrides = (overrides) => {
  cachedOverrides = overrides && typeof overrides === 'object' ? overrides : {};
};

export const getPriceOverrides = () => cachedOverrides;

const cloneDeep = (value) => JSON.parse(JSON.stringify(value));

const coerceOverrideValue = (originalValue, overrideValue) => {
  if (overrideValue === '') {
    return '';
  }

  if (typeof originalValue === 'number') {
    const numeric = Number(overrideValue);
    return Number.isNaN(numeric) ? originalValue : numeric;
  }

  return overrideValue;
};

const applyOverridesToValue = (value, path, overrides) => {
  const overrideValue = overrides[path];
  if (overrideValue === undefined) {
    return value;
  }

  return coerceOverrideValue(value, overrideValue);
};

const walkAndApply = (node, pathPrefix, overrides) => {
  if (Array.isArray(node)) {
    return node.map((item, index) =>
      walkAndApply(item, `${pathPrefix}.${index}`, overrides)
    );
  }

  if (node && typeof node === 'object') {
    const next = { ...node };
    Object.entries(next).forEach(([key, value]) => {
      const path = `${pathPrefix}.${key}`;
      if (PRICE_KEYS.has(key)) {
        next[key] = applyOverridesToValue(value, path, overrides);
      } else {
        next[key] = walkAndApply(value, path, overrides);
      }
    });
    return next;
  }

  return node;
};

export const applyOverridesToData = (moduleKey, data, overrides = cachedOverrides) => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const clone = cloneDeep(data);
  return walkAndApply(clone, moduleKey, overrides);
};

const formatModuleLabel = (moduleKey) =>
  moduleKey
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());

const formatPriceLabel = (key) => {
  switch (key) {
    case 'base_price':
    case 'basePrice':
      return 'Base Price';
    case 'price_adjustment':
    case 'priceAdjustment':
      return 'Price Adjustment';
    default:
      return 'Price';
  }
};

const pickName = (node) => {
  if (!node || typeof node !== 'object') {
    return null;
  }
  for (const key of NAME_KEYS) {
    if (typeof node[key] === 'string' && node[key].trim()) {
      return node[key].trim();
    }
  }
  return null;
};

const walkAndCollect = (node, pathPrefix, overrides, labels, results, moduleLabel) => {
  if (Array.isArray(node)) {
    node.forEach((item, index) => {
      walkAndCollect(item, `${pathPrefix}.${index}`, overrides, labels, results, moduleLabel);
    });
    return;
  }

  if (node && typeof node === 'object') {
    const nameValue = pickName(node);
    const nextLabels = nameValue ? [...labels, nameValue] : labels;

    Object.entries(node).forEach(([key, value]) => {
      const path = `${pathPrefix}.${key}`;
      if (PRICE_KEYS.has(key)) {
        const label = nextLabels.length
          ? `${nextLabels.join(' / ')} - ${formatPriceLabel(key)}`
          : `${moduleLabel} - ${formatPriceLabel(key)}`;
        results.push({
          id: path,
          path,
          label,
          category: moduleLabel,
          currentValue: value,
          overrideValue: overrides[path],
        });
      } else {
        walkAndCollect(value, path, overrides, nextLabels, results, moduleLabel);
      }
    });
  }
};

export const collectPriceFields = (moduleKey, data, overrides = cachedOverrides) => {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const results = [];
  const moduleLabel = formatModuleLabel(moduleKey);
  walkAndCollect(data, moduleKey, overrides, [], results, moduleLabel);
  return results;
};
